/**
 * DEV-ONLY — delete this file before production (with formCSDevPdfExport.js).
 * Also remove the import + render block marked DEV-ONLY in IRASDashboard.js.
 */
import React, { useCallback, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GavelIcon from "@mui/icons-material/Gavel";
import BaseDialog from "../../../utils/dialog/BaseDialog";
import { formatDateLocal } from "../../../../utils/hooks/dataHandler";
import {
  createInitialDeclarationAck,
  isDeclarationStepValid,
} from "./formCSDefaults";
import FormCSDeclarationSection, {
  FORM_CS_DECLARATION_ITEMS,
  FORM_CS_FINAL_CONFIRMATION_LABEL,
} from "./FormCSDeclarationSection";
import { openFormCSPdfInNewTab } from "./FormCSDraftReviewPdf";
import { generateFormCSDraftReviewPdf } from "./Pdf/generator";
import {
  ENABLE_FORM_CS_DEV_PDF_EXPORT,
  buildFormCSDevSubmittedPdfInput,
} from "./formCSDevPdfExport";

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const FormCSDevPdfExportPanel = ({ company, onError }) => {
  const [setupOpen, setSetupOpen] = useState(false);
  const [declarationOpen, setDeclarationOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [payloadJson, setPayloadJson] = useState("");
  const [responseJson, setResponseJson] = useState("");
  const [formSnapshot, setFormSnapshot] = useState(null);
  const [pdfCompany, setPdfCompany] = useState({});
  const [pdfContext, setPdfContext] = useState({});
  const [declaration, setDeclaration] = useState({});
  const [declarationAck, setDeclarationAck] = useState(() =>
    createInitialDeclarationAck()
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [inlineError, setInlineError] = useState("");

  const reportError = useCallback(
    (error, fallback) => {
      const message = error?.message || fallback;
      setInlineError(message);
      onError?.(message);
    },
    [onError]
  );

  const handleOpen = () => {
    setInlineError("");
    setSetupOpen(true);
  };

  const handleContinueToDeclaration = () => {
    setInlineError("");
    try {
      const input = buildFormCSDevSubmittedPdfInput({
        payloadJson,
        responseJson,
        company,
        declarationAck: createInitialDeclarationAck(),
      });
      setPdfCompany(input.company);
      setPdfContext({
        ...input.context,
        submissionDate: new Date().toISOString(),
      });
      setFormSnapshot(input.formSnapshot);
      setDeclaration(input.formSnapshot.declaration);
      setDeclarationAck({
        ...createInitialDeclarationAck(),
        declarationDate: formatDateLocal(new Date()),
      });
      setSetupOpen(false);
      setDeclarationOpen(true);
    } catch (error) {
      reportError(error, "Unable to prepare the Form C-S declaration.");
    }
  };

  const handleSimulatedSubmit = async () => {
    if (!formSnapshot || isSubmitting) return;
    setInlineError("");
    if (!isDeclarationStepValid(declaration, declarationAck)) {
      reportError(
        new Error(
          "Please complete all required declarations and signature fields before submitting. Tax agents must also enter a valid UEN and confirm their authority."
        ),
        "The declaration is incomplete."
      );
      return;
    }

    setIsSubmitting(true);
    try {
      setFormSnapshot((current) => ({
        ...current,
        declaration: { ...declaration },
        declarationAck: { ...declarationAck },
      }));
      setDeclarationOpen(false);
      setShowDetails(false);
      setSuccessOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewSubmittedPdf = () => {
    try {
      const submittedSnapshot = {
        ...formSnapshot,
        declaration: { ...declaration },
        declarationAck: { ...declarationAck },
      };
      const doc = generateFormCSDraftReviewPdf(
        true,
        pdfCompany,
        submittedSnapshot,
        pdfContext
      );
      openFormCSPdfInNewTab(doc, "Form C-S Submitted (DEV)");
    } catch (error) {
      reportError(error, "Unable to generate the submitted Form C-S PDF.");
    }
  };

  const closeSuccess = () => {
    setSuccessOpen(false);
    setShowDetails(false);
  };

  if (!ENABLE_FORM_CS_DEV_PDF_EXPORT) return null;

  return (
    <>
      <Divider />
      <Stack spacing={1.25}>
        <Typography
          variant='overline'
          color='warning.main'
          sx={{ fontWeight: 800, letterSpacing: 0.8 }}
        >
          Dev tools
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          Simulate the final Form C-S declaration, acknowledgement and submitted
          PDF flow. Remove before production.
        </Typography>
        <Button
          size='small'
          variant='outlined'
          color='warning'
          startIcon={<BugReportOutlinedIcon />}
          onClick={handleOpen}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            alignSelf: "flex-start",
          }}
        >
          Simulate Form C-S submission
        </Button>
      </Stack>

      <BaseDialog
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        maxWidth='md'
        fullWidth
        title='DEV: Prepare Form C-S Submission'
        subtitle='Paste the submission payload and mock IRAS response before continuing to the declaration page.'
        icon={<BugReportOutlinedIcon />}
        paperSx={{ overflow: "hidden" }}
        actions={
          <>
            <Button onClick={() => setSetupOpen(false)} color='inherit'>
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant='contained'
              color='warning'
              onClick={handleContinueToDeclaration}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Continue to declaration
            </Button>
          </>
        }
      >
        <Stack spacing={2.5}>
          {inlineError ? <Alert severity='error'>{inlineError}</Alert> : null}
          <Alert severity='warning' sx={{ alignItems: "flex-start" }}>
            DEV-only helper. Uses{" "}
            <strong>{company?.name || "current company"}</strong>
            {company?.financialYearEnd
              ? ` (FYE ${formatDateLocal(new Date(company.financialYearEnd))})`
              : ""}
            . The next screen is the same declaration page used by the live
            submission flow.
          </Alert>
          <TextField
            label='Payload JSON'
            value={payloadJson}
            onChange={(event) => setPayloadJson(event.target.value)}
            multiline
            minRows={8}
            fullWidth
            placeholder='{ "filingInfo": {...}, "dataFormCS": {...}, "declaration": {...} }'
          />
          <TextField
            label='Response JSON'
            value={responseJson}
            onChange={(event) => setResponseJson(event.target.value)}
            multiline
            minRows={6}
            fullWidth
            placeholder='{ "data": { "acknowledgementNo": "...", "estTaxPayable": "..." } }'
          />
        </Stack>
      </BaseDialog>

      <BaseDialog
        open={declarationOpen}
        onClose={() => {
          if (!isSubmitting) setDeclarationOpen(false);
        }}
        closeDisabled={isSubmitting}
        maxWidth='lg'
        title='Declaration & Submission Confirmation'
        subtitle='Confirm authority and accuracy before sending Form C-S to IRAS'
        icon={<GavelIcon />}
        paperSx={{ overflow: "hidden" }}
        actions={
          <>
            <Button
              onClick={() => setDeclarationOpen(false)}
              disabled={isSubmitting}
              color='inherit'
            >
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant='contained'
              color='success'
              onClick={handleSimulatedSubmit}
              disabled={
                isSubmitting ||
                !isDeclarationStepValid(declaration, declarationAck)
              }
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={18} />
                ) : (
                  <AssignmentTurnedInIcon />
                )
              }
              sx={{ textTransform: "none", fontWeight: 700, minWidth: 180 }}
            >
              {isSubmitting ? "Submitting..." : "Confirm & Submit"}
            </Button>
          </>
        }
      >
        {formSnapshot && (
          <Stack spacing={2}>
            {inlineError ? <Alert severity='error'>{inlineError}</Alert> : null}
            <FormCSDeclarationSection
              company={pdfCompany}
              declaration={declaration}
              onChange={setDeclaration}
              declarationAck={declarationAck}
              onAckChange={setDeclarationAck}
              filingInfo={formSnapshot.filingInfo}
              dataFormCS={formSnapshot.dataFormCS}
              selectedTc={null}
              referenceSummary={null}
              disabled={isSubmitting}
            />
          </Stack>
        )}
      </BaseDialog>

      <BaseDialog
        open={successOpen}
        onClose={closeSuccess}
        maxWidth='md'
        title='Form C-S Submitted'
        subtitle='IRAS submission completed successfully.'
        actions={
          <>
            <Box sx={{ flex: 1 }} />
            <Button
              variant='contained'
              onClick={closeSuccess}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              OK
            </Button>
          </>
        }
      >
        <Stack spacing={1.5}>
          {inlineError ? <Alert severity='error'>{inlineError}</Alert> : null}
          <Typography variant='body1' sx={{ fontWeight: 600 }}>
            Form C-S submitted successfully to IRAS.
          </Typography>
          <Box
            sx={(theme) => ({
              p: 1.5,
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
              backgroundColor: alpha(theme.palette.success.main, 0.06),
            })}
          >
            <Stack spacing={0.75}>
              {[
                ["Acknowledgement", pdfContext.acknowledgementNo || "-"],
                ["Estimated Tax Payable", pdfContext.estTaxPayable || "-"],
                [
                  "Submission Date",
                  formatDateTime(pdfContext.submissionDate) || "-",
                ],
              ].map(([label, value]) => (
                <Typography key={label} variant='body2' color='text.secondary'>
                  {label}:{" "}
                  <Box
                    component='span'
                    sx={{ color: "text.primary", fontWeight: 700 }}
                  >
                    {value}
                  </Box>
                </Typography>
              ))}
            </Stack>
          </Box>
          <Button
            variant='outlined'
            startIcon={<DescriptionIcon />}
            onClick={handleViewSubmittedPdf}
            sx={{
              alignSelf: "flex-start",
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            View submitted Form C-S data PDF
          </Button>
          <Box>
            <Button
              color='inherit'
              size='small'
              onClick={() => setShowDetails((current) => !current)}
              endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              aria-expanded={showDetails}
              sx={{ px: 0, textTransform: "none", fontWeight: 700 }}
            >
              {showDetails ? "Hide" : "Show"} declaration and acknowledgement
              details
            </Button>
            <Collapse in={showDetails} unmountOnExit>
              <Stack spacing={2} sx={{ pt: 1.5 }}>
                <Box>
                  <Typography
                    variant='subtitle2'
                    sx={{ mb: 1, fontWeight: 700 }}
                  >
                    IRAS declarations
                  </Typography>
                  <Stack spacing={1}>
                    {FORM_CS_DECLARATION_ITEMS.map((item) => (
                      <Box key={item.key} sx={{ display: "flex", gap: 1 }}>
                        <Chip
                          size='small'
                          color={declaration[item.key] ? "success" : "default"}
                          label={declaration[item.key] ? "Yes" : "No"}
                          sx={{ minWidth: 52, flexShrink: 0, fontWeight: 700 }}
                        />
                        <Typography variant='body2'>
                          {item.label}
                          {item.optional ? " (optional)" : ""}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
                <Divider />
                <Box>
                  <Typography
                    variant='subtitle2'
                    sx={{ mb: 1, fontWeight: 700 }}
                  >
                    Acknowledgement details
                  </Typography>
                  <Stack spacing={1.25}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Chip
                        size='small'
                        color='success'
                        label='Yes'
                        sx={{ minWidth: 52, flexShrink: 0, fontWeight: 700 }}
                      />
                      <Typography variant='body2'>
                        {FORM_CS_FINAL_CONFIRMATION_LABEL}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        Submission Authority
                      </Typography>
                      <Typography variant='body2' sx={{ fontWeight: 700 }}>
                        {declarationAck.submitterCapacity === "TAX_AGENT"
                          ? `Authorised Tax Agent — ${declarationAck.taxAgentUen}`
                          : "Taxpayer / Company Representative"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        Declaration name
                      </Typography>
                      <Typography variant='body2' sx={{ fontWeight: 700 }}>
                        {declarationAck.declarantName || "-"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        Date of declaration
                      </Typography>
                      <Typography variant='body2' sx={{ fontWeight: 700 }}>
                        {declarationAck.declarationDate || "-"}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Collapse>
          </Box>
          <Typography variant='caption' color='text.secondary'>
            DEV simulation only. No data was sent to IRAS.
          </Typography>
        </Stack>
      </BaseDialog>
    </>
  );
};

export default FormCSDevPdfExportPanel;
